<script setup>
import { AdyenCheckout, GooglePay, ApplePay } from '@adyen/adyen-web';
import { inject, onMounted, useTemplateRef } from 'vue';
import csrfToken from '@cta/shared/csrf';
import { CMPR_INTERACT_EVENT, dispatchCmprEvent } from '@campact/camper/utils';

let paymentMethods;

const googlePayRef = useTemplateRef('googlePayRef');
const applePayRef = useTemplateRef('applePayRef');

const props = defineProps({
  adyenSettings: {
    type: Object,
    default() {
      return {
        clientKey: undefined,
        environment: undefined,
        gatewayMerchantId: undefined,
        googlePayMerchantId: undefined,
        merchantName: undefined,
        mobilePaymentTypes: {
          type: Array,
          default: () => [],
        },
        paymentPath: undefined,
        paymentMethodsPath: undefined,
      };
    },
  },
});

const emit = defineEmits(['submit']);

const store = inject('store');

async function fetchPaymentMethods() {
  return (await fetch(props.adyenSettings['paymentMethodsPath'], {
    headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
  })).json();
}

async function paymentTypeConfiguration(paymentType) {
  if (paymentMethods === undefined) {
    paymentMethods = await fetchPaymentMethods();
  }

  const paymentMethod = paymentMethods.paymentMethods.find((method) => method.type === paymentType.replace('_', ''));
  if (paymentMethod === undefined) {
    throw new Error(`Payment type ${paymentType} not offered by Adyen.`);
  }

  return { ...paymentMethod.configuration, merchantName: props.adyenSettings['merchantName'] };
}

function globalConfiguration() {
  return {
    environment: props.adyenSettings['environment'],
    amount: {
      value: store.values.amount,
      currency: 'EUR',
    },
    locale: 'de-DE',
    clientKey: props.adyenSettings['clientKey'],
    countryCode: 'DE',
    onPaymentCompleted: () => {
      emit('submit');
    },
  };
}

async function paymentConfiguration(paymentRef, paymentType) {
  return {
    buttonType: 'donate',
    configuration: await paymentTypeConfiguration(paymentType),
    onClick: (resolve) => {
      store.values.paymentType = paymentType;
      dispatchCmprEvent(paymentRef.value, CMPR_INTERACT_EVENT, { event: 'cmpr-button-click' });
      resolve();
    },
    onSubmit: async (status, component, actions) => {
      if (!status.isValid) {
        store.values.adyenData = undefined;
        actions.reject();
        return;
      }

      const formData = new FormData();
      formData.append('amount', store.values.amount);
      formData.append('adyen_data', JSON.stringify(status.data));
      formData.append('first_name', store.values.firstname);
      formData.append('last_name', store.values.lastname);
      formData.append('email_address', store.values.emailaddress);
      const response = await fetch(props.adyenSettings['paymentPath'], {
        method: 'post',
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() },
        body: formData,
      });

      if (response.ok) {
        const json = await response.json();
        store.values.adyenData = json;
        actions.resolve({ resultCode: json.resultCode });
      } else {
        store.values.adyenData = undefined;
        actions.reject();
      }
    },
    onError: () => {
      store.values.paymentType = undefined;
    },
  };
}

async function initAdyen() {
  window.adyenCheckout = await AdyenCheckout(globalConfiguration());

  if (props.adyenSettings['mobilePaymentTypes'].includes('google_pay')) {
    const googlePay = new GooglePay(window.adyenCheckout, await paymentConfiguration(googlePayRef, 'google_pay'));
    googlePay
      .isAvailable()
      .then(() => {
        googlePay.mount('#payment-container--google_pay');
      })
      .catch((e) => {
        console.log('Google Pay is not available', e);
        googlePayRef.value.remove();
      });
  }

  if (props.adyenSettings['mobilePaymentTypes'].includes('apple_pay')) {
    const applePay = new ApplePay(window.adyenCheckout, await paymentConfiguration(applePayRef, 'apple_pay'));
    applePay
      .isAvailable()
      .then(() => {
        applePay.mount('#payment-container--apple_pay');
      })
      .catch((e) => {
        console.log('Apple Pay is not available', e);
        applePayRef.value.remove();
      });
  }
}

onMounted(() => {
  initAdyen();
});
</script>

<template>
  <div
    class="payment-container"
  >
    <div
      v-if="props.adyenSettings['mobilePaymentTypes'].includes('apple_pay')"
      id="payment_type_apple_pay"
      ref="applePayRef"
      class="payment-container__payment-type"
      data-analytics-camper-web-component="apple-pay-button"
      data-analytics-context="payment-step"
      data-analytics-inner-text="Mit Apple Pay spenden"
    >
      <div id="payment-container--apple_pay" />
    </div>

    <div
      v-if="props.adyenSettings['mobilePaymentTypes'].includes('google_pay')"
      id="payment_type_google_pay"
      ref="googlePayRef"
      class="payment-container__payment-type"
      data-analytics-camper-web-component="google-pay-button"
      data-analytics-context="payment-step"
      data-analytics-inner-text="Mit Google Pay spenden"
    >
      <component
        :is="'script'"
        src="https://pay.google.com/gp/p/js/pay.js"
      />

      <div id="payment-container--google_pay" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  @import '@cta/frontend/stylesheets/custom-media';

  .payment-container {
    display: flex;
    flex-wrap: wrap;
    gap: var(--cmpr-space-60);
  }

  .payment-container__payment-type {
    flex: 1;
    height: 48px;
  }
</style>
